import React, { useState, useEffect } from 'react';
import { supabase } from '../services/supabaseService';
import { LayoutTemplate, Edit2, Trash2, Plus, Loader2 } from 'lucide-react';
import { User } from '../types';

export interface VacancyTemplate {
    id: string;
    client_id: string;
    name: string;
    content: any;
    created_at: string;
}

interface VacancyTemplatesProps {
    user: User;
}

const VacancyTemplates: React.FC<VacancyTemplatesProps> = ({ user }) => {
    const [templates, setTemplates] = useState<VacancyTemplate[]>([]);
    const [loading, setLoading] = useState(true);

    const fetchTemplates = async () => {
        if (!user?.clientId) return;
        setLoading(true);
        const { data, error } = await supabase
            .from('vacancy_templates')
            .select('*')
            .eq('client_id', user.clientId)
            .order('created_at', { ascending: false });

        if (!error && data) {
            setTemplates(data as unknown as VacancyTemplate[]);
        }
        setLoading(false);
    };

    useEffect(() => {
        fetchTemplates();
    }, [user?.clientId]);

    const handleDelete = async (id: string, name: string) => {
        if (!window.confirm(`Weet je zeker dat je het sjabloon "${name}" wilt verwijderen?`)) return;
        
        const { error } = await supabase.from('vacancy_templates').delete().eq('id', id);
        if (error) {
            alert('Fout bij het verwijderen van het sjabloon.');
        } else {
            fetchTemplates();
        }
    };

    return (
        <div className="p-8 max-w-7xl mx-auto space-y-8 pb-32 pt-[100px] md:pt-8 min-h-screen">
            <header className="flex flex-col md:flex-row justify-between items-start md:items-end gap-6 mb-12 relative z-20">
                <div className="w-full">
                    <div className="flex items-center gap-3">
                        <div className="h-10 w-2.5 bg-[#e51b23] rounded-r-full shadow-lg -ml-8"></div>
                        <div>
                            <h1 className="text-4xl font-black text-slate-900 tracking-tight flex items-center gap-3">
                                <LayoutTemplate className="text-slate-400" size={32} />
                                Vacature Templates
                            </h1>
                            <p className="text-slate-500 font-medium text-sm mt-1">Beheer herbruikbare structuren en content sjablonen voor je vacatures.</p>
                        </div>
                    </div>
                </div>
            </header>

            {loading ? (
                <div className="flex justify-center items-center py-20">
                    <Loader2 className="animate-spin text-[#e51b23]" size={32} />
                </div>
            ) : templates.length === 0 ? (
                <div className="bg-white rounded-2xl p-12 text-center border border-slate-200 shadow-sm">
                    <div className="w-16 h-16 bg-red-50 rounded-full flex items-center justify-center mx-auto mb-4">
                        <LayoutTemplate size={24} className="text-[#e51b23]" />
                    </div>
                    <h3 className="text-lg font-bold text-slate-900 mb-2">Geen templates gevonden</h3>
                    <p className="text-slate-500 mb-6">Je hebt nog geen vacature sjablonen aangemaakt. Ga naar de Vacature Builder om een bestaande vacature op te slaan als sjabloon.</p>
                </div>
            ) : (
                <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
                    {templates.map(t => (
                        <div key={t.id} className="bg-white rounded-2xl border border-slate-200 shadow-sm p-6 flex flex-col hover:shadow-md transition-shadow">
                            <div className="flex justify-between items-start mb-4">
                                <div className="p-3 bg-red-50 rounded-xl">
                                    <LayoutTemplate size={24} className="text-[#e51b23]" />
                                </div>
                                <button 
                                    onClick={() => handleDelete(t.id, t.name)}
                                    className="p-2 text-slate-400 hover:text-red-500 hover:bg-red-50 rounded-lg transition-colors"
                                >
                                    <Trash2 size={16} />
                                </button>
                            </div>
                            <h3 className="text-lg font-bold text-slate-900 mb-1">{t.name}</h3>
                            <p className="text-xs font-medium text-slate-500 mb-4 uppercase tracking-widest">{new Date(t.created_at || '').toLocaleDateString('nl-NL')}</p>
                            
                            <div className="flex-1">
                                <p className="text-sm text-slate-600 font-medium mb-2">Secties:</p>
                                <div className="flex flex-wrap gap-2">
                                    {Array.isArray(t.content) && t.content.slice(0, 4).map((c: any, i: number) => (
                                        <span key={i} className="px-2 py-1 bg-slate-100 text-slate-600 rounded text-[10px] font-bold uppercase tracking-wider">
                                            {c.title}
                                        </span>
                                    ))}
                                    {Array.isArray(t.content) && t.content.length > 4 && (
                                        <span className="px-2 py-1 bg-slate-50 text-slate-400 rounded text-[10px] font-bold uppercase tracking-wider">
                                            +{t.content.length - 4} meer
                                        </span>
                                    )}
                                </div>
                            </div>
                        </div>
                    ))}
                </div>
            )}
        </div>
    );
}

export default VacancyTemplates;
